import React from "react";
import {
  Typography,
  Button,
  Grid,
  Avatar,
  Stack,
  Box,
  Container,
  Divider,
  Link
} from "@mui/material";
import { LinkedIn, Article, ArrowForward, YouTube } from "@mui/icons-material";
import TechChip from '../components/TechChip';
import RailBox from '../components/RailBox';

const YOUTUBE_URL = "https://www.youtube.com/@tech.with.mahendra";

const ExperienceItem = ({ company, role, duration, description, link }) => (
  <RailBox sx={{ mb: 4, position: 'relative', pl: 3, '&:hover': { borderColor: 'text.primary' }, transition: 'all 0.3s' }}>
    <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
      {company}
    </Typography>
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic' }}>
      {role} • {duration}
    </Typography>
    <Typography variant="body2" color="text.secondary" component="div">
      {description}
    </Typography>
  </RailBox>
);

const Home = () => {
  return (
    <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>
      {/* Hero Section */}
      <Grid container spacing={6} alignItems="center" sx={{ mb: 12 }}>
        <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <Avatar
              alt="Mahendra Chouhan"
              src="/logo.jpg"
              sx={{ 
                width: '100%', 
                height: 'auto',
                aspectRatio: '1/1',
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'text.primary',
                bgcolor: 'background.paper'
              }}
            />
          </Box>
        </Grid>
        
        <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
            Ex-Google, Ex-Atlassian · Now teaching what I build
          </Typography>
          <Typography variant="h1" gutterBottom sx={{ mb: 3 }}>
            Engineering <br/>
            <Box
              component="span"
              sx={{
                background: (theme) => `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              for Scale.
            </Box>
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4, maxWidth: 600 }}>
            My programming journey began with a simple snake game in a school lab. 
            That spark became an obsession with solving real-world problems through code. 
            After <b>10+ years of engineering</b> at companies like <b>Google</b> and <b>Atlassian</b>,
            I'm now exploring education: explaining how software and AI really work through my YouTube channel,
            in-depth blog posts, and small interactive projects you can play with.
          </Typography>

          <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap' }}>
            {['System Design', 'Cloud Architecture', 'Game Development', 'Full Stack'].map(skill => (
              <TechChip key={skill} label={skill} />
            ))}
          </Box>
          
          <Stack spacing={2} direction="row" useFlexGap flexWrap="wrap">
            <Button 
              variant="contained" 
              color="primary" 
              disableElevation
              endIcon={<ArrowForward />}
              href="mailto:mchouhanofficial@gmail.com"
            >
              Get in touch
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<LinkedIn />}
              href="https://www.linkedin.com/in/mchouhanofficial/"
            >
              LinkedIn
            </Button>
            <Button
              variant="outlined"
              startIcon={<YouTube />}
              href={YOUTUBE_URL}
            >
              YouTube
            </Button>
            <Button 
              variant="text" 
              startIcon={<Article />}
              href="https://medium.com/@mchouhanofficial"
            >
              Writings
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Now */}
      <Grid container spacing={6} sx={{ mb: 10 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2" gutterBottom>
            Now
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <ExperienceItem
            company={<Link href={YOUTUBE_URL} color="inherit" underline="hover">YouTube · Tech with Mahendra</Link>}
            role="Creator"
            duration="2026 – Present"
            description="Short, visual explanations of software engineering and AI, built from first principles."
          />
          <ExperienceItem
            company={<Link href="/blogs/" color="inherit" underline="hover">Blog</Link>}
            role="Writer"
            duration="2026 – Present"
            description="Long-form, interactive posts, starting with a series that builds a GPT from scratch one idea at a time."
          />
          <ExperienceItem
            company="Education"
            role="Exploring"
            duration="2026 – Present"
            description="Figuring out how people best learn to build software."
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 10 }} />

      {/* Education */}
      <Grid container spacing={6} sx={{ mb: 10 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2" gutterBottom>
            Education
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <ExperienceItem 
            company="IIT Kharagpur"
            role="M.Tech, Computer Science"
            duration="2016"
            description="Specialized in Computer Science and Engineering."
          />
          <ExperienceItem 
            company="IIT BHU"
            role="B.Tech, Electronics"
            duration="2014"
            description="Bachelor of Technology in Electronics Engineering."
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 10 }} />

      {/* Experience Section */}
      <Grid container spacing={6} sx={{ mb: 12 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2" gutterBottom>
            Career<br/>Path
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <ExperienceItem 
            company="Atlassian"
            role="Senior Software Engineer (P50) | Backend"
            duration="June 2024 – September 2026"
            description="Led AI agent development for Jira Service Management handling 10k daily queries, helping improve resolution rates by 5% through enterprise integrations."
          />
          <ExperienceItem 
            company="Google"
            role="Software Engineer"
            duration="4+ years"
            description="Contributed and maintained gCMS, a content management system for Google Store e-commerce business."
          />
          <ExperienceItem 
            company="Moonfrog Labs"
            role="Full Stack Engineer"
            duration="1.5 years"
            description="Enhanced Teen Patti Gold, a real-time card game with over 3M daily users."
          />
          <ExperienceItem 
            company="Ola Cabs"
            role="Software Engineer"
            duration="1 Year"
            description="Worked on Ola Play, focusing on scalability and performance optimization."
          />
          <ExperienceItem 
            company="AppDynamics (Cisco)"
            role="Software Engineer"
            duration="1 Year"
            description="Contributed to DbMon, a database monitoring product by AppDynamics."
          />
        </Grid>
      </Grid>


    </Container>
  );
};

export default Home;
